import { useEffect, useMemo, useState } from "react";
import AlertComponent from "../../components/AlertComponent";
import {
  excluirAgendamentoMensagem,
  excluirModeloMensagem,
  listarAgendamentosMensagem,
  listarModelosMensagem,
  processarAgendamentoMensagem,
  salvarAgendamentoMensagem,
  salvarModeloMensagem,
} from "../../services/mensagemService";

const emptyModelo = {
  id: null,
  nome: "",
  corpo: "Olá {{nome}}! Faltam {{dias}} dias para {{nome_evento}} em {{data}}. Seu código é {{codigo}}. Acesse: {{url_evento}}",
  ativo: true,
};

const emptyAgendamento = {
  id: null,
  mensagemModeloId: "",
  nome: "",
  agendadaPara: "",
  publicoAlvo: 0,
  ativo: true,
};

const VARIAVEIS = [
  "{{nome}}",
  "{{nome_evento}}",
  "{{nome_espaco}}",
  "{{localizacao}}",
  "{{descricao}}",
  "{{dias}}",
  "{{data}}",
  "{{data_limite_confirmacao}}",
  "{{codigo}}",
  "{{url_evento}}",
  "{{link_evento_sem_codigo}}",
];
const PUBLICOS_ALVO = [
  { value: 0, label: "Todos" },
  { value: 1, label: "Não confirmados" },
  { value: 2, label: "Confirmados que vão" },
  { value: 3, label: "Confirmados que recusaram" },
];

export default function MensagensPanel({ eventId }) {
  const [modelos, setModelos] = useState([]);
  const [agendamentos, setAgendamentos] = useState([]);
  const [modeloForm, setModeloForm] = useState(emptyModelo);
  const [agendamentoForm, setAgendamentoForm] = useState(emptyAgendamento);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [processandoId, setProcessandoId] = useState(null);

  const modeloSelecionado = useMemo(
    () => modelos.find((modelo) => modelo.id === Number(agendamentoForm.mensagemModeloId)),
    [agendamentoForm.mensagemModeloId, modelos]
  );

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  async function carregar() {
    setLoading(true);
    try {
      const [modelosRes, agendamentosRes] = await Promise.all([
        listarModelosMensagem(eventId),
        listarAgendamentosMensagem(eventId),
      ]);
      setModelos(modelosRes);
      setAgendamentos(agendamentosRes);
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", "Erro ao carregar mensagens", { icon: "error" });
    } finally {
      setLoading(false);
    }
  }

  function atualizarModelo(campo, valor) {
    setModeloForm((current) => ({ ...current, [campo]: valor }));
  }

  function inserirVariavel(variavel) {
    setModeloForm((current) => ({ ...current, corpo: `${current.corpo || ""}${variavel}` }));
  }

  async function salvarModelo(e) {
    e.preventDefault();
    if (!modeloForm.nome.trim() || !modeloForm.corpo.trim()) {
      AlertComponent("Campos obrigatórios", "Informe nome e mensagem do modelo", { icon: "warning" });
      return;
    }

    setSalvando(true);
    try {
      await salvarModeloMensagem(eventId, {
        id: modeloForm.id,
        nome: modeloForm.nome.trim(),
        corpo: modeloForm.corpo.trim(),
        ativo: modeloForm.ativo,
      });
      setModeloForm(emptyModelo);
      await carregar();
      AlertComponent("Sucesso", "Modelo salvo", { icon: "success", timer: 1500 });
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err.response?.data?.mensagem || "Erro ao salvar modelo", { icon: "error" });
    } finally {
      setSalvando(false);
    }
  }

  async function removerModelo(modelo) {
    if (!window.confirm(`Remover o modelo "${modelo.nome}"?`)) return;

    setSalvando(true);
    try {
      await excluirModeloMensagem(eventId, modelo.id);
      await carregar();
      AlertComponent("Removido", "Modelo removido", { icon: "success", timer: 1300 });
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err.response?.data?.mensagem || "Erro ao remover modelo", { icon: "error" });
    } finally {
      setSalvando(false);
    }
  }

  function editarModelo(modelo) {
    setModeloForm({
      id: modelo.id,
      nome: modelo.nome || "",
      corpo: modelo.corpo || "",
      ativo: modelo.ativo,
    });
  }

  function atualizarAgendamento(campo, valor) {
    setAgendamentoForm((current) => ({ ...current, [campo]: valor }));
  }

  async function salvarAgendamento(e) {
    e.preventDefault();
    if (!agendamentoForm.mensagemModeloId || !agendamentoForm.agendadaPara) {
      AlertComponent("Campos obrigatórios", "Selecione modelo e data de envio", { icon: "warning" });
      return;
    }

    setSalvando(true);
    try {
      await salvarAgendamentoMensagem(eventId, {
        id: agendamentoForm.id,
        mensagemModeloId: Number(agendamentoForm.mensagemModeloId),
        nome: agendamentoForm.nome.trim() || null,
        agendadaPara: agendamentoForm.agendadaPara,
        publicoAlvo: Number(agendamentoForm.publicoAlvo),
        ativo: agendamentoForm.ativo,
      });
      setAgendamentoForm(emptyAgendamento);
      await carregar();
      AlertComponent("Sucesso", "Agendamento salvo", { icon: "success", timer: 1500 });
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err.response?.data?.mensagem || "Erro ao salvar agendamento", { icon: "error" });
    } finally {
      setSalvando(false);
    }
  }

  function editarAgendamento(agendamento) {
    setAgendamentoForm({
      id: agendamento.id,
      mensagemModeloId: agendamento.mensagemModeloId,
      nome: agendamento.nome || "",
      agendadaPara: agendamento.agendadaPara ? agendamento.agendadaPara.slice(0, 16) : "",
      publicoAlvo: agendamento.publicoAlvo ?? 0,
      ativo: agendamento.ativo,
    });
  }

  async function removerAgendamento(agendamento) {
    if (!window.confirm("Remover este agendamento?")) return;

    setSalvando(true);
    try {
      await excluirAgendamentoMensagem(eventId, agendamento.id);
      await carregar();
      AlertComponent("Removido", "Agendamento removido", { icon: "success", timer: 1300 });
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err.response?.data?.mensagem || "Erro ao remover agendamento", { icon: "error" });
    } finally {
      setSalvando(false);
    }
  }

  async function processar(agendamento) {
    setProcessandoId(agendamento.id);
    try {
      const res = await processarAgendamentoMensagem(eventId, agendamento.id);
      await carregar();
      AlertComponent(
        "Processado",
        `${res.agendados || 0} enviado(s) ao Gateway, ${res.ignoradosPublicoAlvo || 0} fora do público, ${res.falhasRetentaveis || 0} falha(s) temporária(s), ${res.falhasNaoRetentaveis || 0} falha(s) definitiva(s)`,
        { icon: "success", timer: 2800 }
      );
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err.response?.data?.mensagem || "Erro ao processar agendamento", { icon: "error" });
    } finally {
      setProcessandoId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[420px_minmax(0,1fr)] gap-6">
      <div className="space-y-6">
        <form onSubmit={salvarModelo} className="bg-white p-5 rounded-xl shadow border space-y-4">
          <div>
            <h2 className="text-base font-semibold text-slate-800">Modelo de mensagem</h2>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nome</label>
            <input
              type="text"
              value={modeloForm.nome}
              onChange={(e) => atualizarModelo("nome", e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
              disabled={salvando}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Mensagem</label>
            <textarea
              rows={7}
              value={modeloForm.corpo}
              onChange={(e) => atualizarModelo("corpo", e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400 resize-y"
              disabled={salvando}
            />
            <div className="mt-2 flex flex-wrap gap-2">
              {VARIAVEIS.map((variavel) => (
                <button
                  key={variavel}
                  type="button"
                  onClick={() => inserirVariavel(variavel)}
                  className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                  disabled={salvando}
                >
                  {variavel}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={modeloForm.ativo}
              onChange={(e) => atualizarModelo("ativo", e.target.checked)}
              disabled={salvando}
            />
            Ativo
          </label>
          <div className="flex justify-end gap-2">
            {modeloForm.id && (
              <button
                type="button"
                onClick={() => setModeloForm(emptyModelo)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                disabled={salvando}
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
              disabled={salvando}
            >
              {salvando ? "Salvando..." : modeloForm.id ? "Atualizar modelo" : "Criar modelo"}
            </button>
          </div>
        </form>

        <form onSubmit={salvarAgendamento} className="bg-white p-5 rounded-xl shadow border space-y-4">
          <h2 className="text-base font-semibold text-slate-800">Agendamento</h2>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Modelo</label>
            <select
              value={agendamentoForm.mensagemModeloId}
              onChange={(e) => atualizarAgendamento("mensagemModeloId", e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
              disabled={salvando}
            >
              <option value="">Selecione</option>
              {modelos.map((modelo) => (
                <option key={modelo.id} value={modelo.id}>
                  {modelo.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nome</label>
            <input
              type="text"
              value={agendamentoForm.nome}
              onChange={(e) => atualizarAgendamento("nome", e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
              placeholder={modeloSelecionado?.nome || "Opcional"}
              disabled={salvando}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Data e hora</label>
            <input
              type="datetime-local"
              value={agendamentoForm.agendadaPara}
              onChange={(e) => atualizarAgendamento("agendadaPara", e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
              disabled={salvando}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Enviar para</label>
            <select
              value={agendamentoForm.publicoAlvo}
              onChange={(e) => atualizarAgendamento("publicoAlvo", Number(e.target.value))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
              disabled={salvando}
            >
              {PUBLICOS_ALVO.map((publico) => (
                <option key={publico.value} value={publico.value}>
                  {publico.label}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={agendamentoForm.ativo}
              onChange={(e) => atualizarAgendamento("ativo", e.target.checked)}
              disabled={salvando}
            />
            Ativo
          </label>
          <div className="flex justify-end gap-2">
            {agendamentoForm.id && (
              <button
                type="button"
                onClick={() => setAgendamentoForm(emptyAgendamento)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                disabled={salvando}
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
              disabled={salvando || modelos.length === 0}
            >
              {salvando ? "Salvando..." : agendamentoForm.id ? "Atualizar agendamento" : "Criar agendamento"}
            </button>
          </div>
        </form>
      </div>

      <div className="space-y-6 min-w-0">
        <section className="bg-white rounded-xl shadow border overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="text-base font-semibold text-slate-800">Modelos cadastrados</h2>
          </div>
          {modelos.length === 0 ? (
            <div className="px-5 py-10 text-sm text-slate-500 text-center">Nenhum modelo cadastrado.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {modelos.map((modelo) => (
                <div key={modelo.id} className="p-5">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-800">{modelo.nome}</p>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${modelo.ativo ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {modelo.ativo ? "Ativo" : "Inativo"}
                        </span>
                      </div>
                      <p className="mt-2 whitespace-pre-wrap text-sm text-slate-500">{modelo.corpo}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button type="button" onClick={() => editarModelo(modelo)} className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200">
                        Editar
                      </button>
                      <button type="button" onClick={() => removerModelo(modelo)} className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100">
                        Remover
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white rounded-xl shadow border overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="text-base font-semibold text-slate-800">Agendamentos</h2>
          </div>
          {agendamentos.length === 0 ? (
            <div className="px-5 py-10 text-sm text-slate-500 text-center">Nenhum agendamento cadastrado.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Agendamento</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Modelo</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Público</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Disparos</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {agendamentos.map((agendamento) => (
                    <tr key={agendamento.id}>
                      <td className="px-4 py-4">
                        <p className="text-sm font-semibold text-slate-800">{agendamento.nome || agendamento.modelo?.nome || "Mensagem"}</p>
                        <p className="text-xs text-slate-500">{formatarData(agendamento.agendadaPara)}</p>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-600">{agendamento.modelo?.nome || "-"}</td>
                      <td className="px-4 py-4 text-sm text-slate-600">{agendamento.publicoAlvoNome || obterPublicoAlvoNome(agendamento.publicoAlvo)}</td>
                      <td className="px-4 py-4 text-center text-sm font-semibold text-slate-700">{agendamento.totalDisparos || 0}</td>
                      <td className="px-4 py-4 text-center">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${agendamento.ativo ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {agendamento.ativo ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex justify-center gap-2">
                          <button type="button" onClick={() => processar(agendamento)} disabled={processandoId === agendamento.id} className="rounded-lg bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-60">
                            {processandoId === agendamento.id ? "Processando..." : "Processar"}
                          </button>
                          <button type="button" onClick={() => editarAgendamento(agendamento)} className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200">
                            Editar
                          </button>
                          <button type="button" onClick={() => removerAgendamento(agendamento)} className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100">
                            Remover
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function obterPublicoAlvoNome(value) {
  return PUBLICOS_ALVO.find((publico) => publico.value === Number(value))?.label || "Todos";
}

function formatarData(value) {
  if (!value) return "-";

  return new Date(value).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
